import React, { FC, ReactElement, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Divider, Modal as AntModal, InputNumber, Tag, Typography } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import TourIcon from './TourIcon'
import { ArrowCarousel, LoadError } from '../../../Components/UI'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import _ from 'lodash'
import { s } from './style'
import './styles.less'
import { formatCents, openGoogleMaps } from '../../../Utilities'
import { getAddress } from '../../../Graphql-Utilities'
import { ROUTES } from '../../../Constants'
import {
  ProposalModalProposalFragment,
  ProposalModalEventFragment,
  useProposalModalRequestToBookMutation,
  ExtraCostItemFragment,
} from './ProposalModal.generated-types'
import ReactMarkdown from 'react-markdown'

const { Link, Paragraph, Text, Title } = Typography

type OptionalExtras = Omit<ExtraCostItemFragment, '__typename'>

interface ProposalModalProps {
  modalVisible: boolean
  handleCloseModal: () => void
  canRequestToBook: boolean
  response: ProposalModalProposalFragment
  event: ProposalModalEventFragment
}

const spatialConfigurationLabels: Record<string, string> = {
  boardroom: 'Boardroom',
  cabaret: 'Cabaret',
  classroom: 'Classroom',
  seating: 'Seating',
  standing: 'Standing',
  theatre: 'Theater style',
  'u-shaped': 'U-shaped',
  dinner_rounds: 'Dinner / Rounds',
}

const ProposalModalWithoutRouting: FC<ProposalModalProps & RouteComponentProps> = ({
  history,
  modalVisible,
  handleCloseModal,
  canRequestToBook,
  response,
  response: {
    ID,
    space: {
      spaceName,
      description,
      venue,
      spaceFeatures,
      spacePhotos,
      tour3dUrl,
      spatialConfigurations,
    },
    currency,
    baseCostTotal,
    baseCostDescription,
    extraCostItems,
    message,
    documents,
  },
  event,
  // event: { accountManager },
}): ReactElement => {
  const { nearestTransit } = venue
  const address = getAddress(response)
  const { t } = useTranslation()

  const [optionalExtras, setOptionalExtras] = useState<OptionalExtras[]>([
    { ID: 0, description: '', costPerUnit: 0, quantity: 0 },
  ])

  useEffect(() => {
    setOptionalExtras(
      extraCostItems.map(({ ID, description, costPerUnit, quantity }) => ({
        ID,
        description,
        costPerUnit,
        quantity: quantity || 0,
      }))
    )
  }, [extraCostItems])

  const optionalExtrasTotal = _.sum(
    optionalExtras.map(({ quantity, costPerUnit }) => quantity * costPerUnit)
  )

  const goToConfirmation = (): void =>
    history.push({
      pathname: ROUTES.CONFIRMATION_V2,
    })

  const [{ fetching, error }, requestToBook] = useProposalModalRequestToBookMutation()

  const handleRequestToBook = async (): Promise<void> => {
    const { error } = await requestToBook({
      input: {
        proposalID: ID,
        costItems: optionalExtras.map(({ ID, quantity }) => ({
          ID,
          quantity,
        })),
      },
    })

    if (!error) {
      handleCloseModal()
      goToConfirmation()
    }
  }

  const handleOptionalExtraChange = (newQuantity: number, ID: number): void => {
    const index = optionalExtras.findIndex((quant) => quant.ID === ID)
    const newQuantities = [...optionalExtras]
    newQuantities[index].quantity = newQuantity
    setOptionalExtras(newQuantities)
  }

  return (
    <AntModal
      className='proposal-modal'
      visible={modalVisible}
      centered
      footer={null}
      onCancel={handleCloseModal}
      bodyStyle={s.modalStyle}
      width={1074}>
      <ArrowCarousel>
        {spacePhotos.map((photo, index) => (
          <span key={photo.url}>
            <img src={photo.url} alt={`Space photo ${index + 1}`} style={s.image} />
          </span>
        ))}
      </ArrowCarousel>
      <div style={s.header}>
        <Title level={3} style={s.venueTitle}>
          {venue.name}
        </Title>
        <Title level={4} style={s.spaceTitle}>
          {spaceName}
        </Title>
      </div>
      <div style={s.main}>
        <div style={s.content}>
          <div style={s.spaceHeader.root}>
            <Title level={5} style={s.spaceHeader.about}>
              {t('ProposalModal.aboutSpace', 'About the space')}
            </Title>
            {tour3dUrl && (
              <span style={s.spaceHeader.tour}>
                <TourIcon width={28} height={28} />
                <Link href={`${tour3dUrl}`} target='_blank' style={s.spaceHeader.tourLink}>
                  {t('proposal.virtualTour', 'Virtual tour')}
                </Link>
              </span>
            )}
          </div>
          <div style={s.contentRow}>
            <Text strong>{t('ProposalModal.capacity', 'Capacity')}</Text>
          </div>
          <div style={s.contentRow}>
            {spatialConfigurations.map((configuration) => (
              <div key={configuration.name}>
                <Text>{spatialConfigurationLabels[configuration.name]}: </Text>
                <Text style={s.spatialConfigurationLabels}>{configuration.capacity} </Text>
              </div>
            ))}
          </div>
          {address && (
            <div style={s.contentRow}>
              <Text strong>{t('ProposalModal.location', 'Location')}</Text>
              <Text>{address}</Text>
              <Link onClick={() => openGoogleMaps(address)}>
                {t('ProposalModal.viewOnMap', 'View on a map')}
              </Link>
            </div>
          )}
          {nearestTransit?.station && (
            <div style={s.contentRow}>
              <Text strong>{t('ProposalModal.nearestTransport', 'Nearest transport')}</Text>
              <Text>{nearestTransit.station}</Text>
            </div>
          )}
          {spaceFeatures && (
            <div style={s.featuresRow}>
              <Text strong>{t('ProposalModal.facilities', 'Facilities')}</Text>
              {spaceFeatures.map((feature) => (
                <Tag key={feature.ID}>{feature.name}</Tag>
              ))}
            </div>
          )}
          {documents && documents.length > 0 && (
            <div style={s.contentColumn}>
              <Text strong style={s.columnTitle}>
                {t('ProposalModal.attachments', 'Attachments')}
              </Text>
              {documents.map((document) => (
                <Link href={`https://${document.URL}`} target='_blank' key={document.ID}>
                  <DownloadOutlined style={s.downloadIcon} />
                  {document.filename}
                </Link>
              ))}
            </div>
          )}
          {message && (
            <div style={s.contentColumn}>
              <Text strong style={s.columnTitle}>
                {t('ProposalModal.venueMessage', 'Message from the venue')}
              </Text>
              <Text>
                <ReactMarkdown>{message}</ReactMarkdown>
              </Text>
            </div>
          )}
          {description && (
            <div style={s.contentColumn}>
              <Text strong style={s.columnTitle}>
                {t('ProposalModal.spaceDescription', 'Space description')}
              </Text>
              <Paragraph>{description}</Paragraph>
            </div>
          )}
        </div>
        <div style={s.sidebar.root}>
          <Title level={5} style={s.sidebar.title}>
            {t('ProposalModal.costs', 'Costs')}
          </Title>
          <div style={s.sidebar.costs}>
            <Text strong>{t('ProposalModal.fixedCost', 'Fixed cost')}</Text>
            <Text style={s.sidebar.value}>{formatCents(currency, baseCostTotal)}</Text>
          </div>
          <Text style={s.sidebar.costDescription}>
            <ReactMarkdown>{baseCostDescription}</ReactMarkdown>
          </Text>
          {extraCostItems && (
            <>
              <Title level={5} style={s.sidebar.optionalExtras.title}>
                {t('proposal.requestToBook.optionalExtras', 'Optional extras')}
              </Title>
              {optionalExtras.map(({ ID, description, costPerUnit, quantity }) => (
                <div style={s.sidebar.costs} key={ID}>
                  <Text>{description}</Text>
                  <div style={s.sidebar.optionalExtras.value}>
                    <Text>{`${formatCents(currency, costPerUnit)} x `}</Text>
                    <InputNumber
                      disabled={!canRequestToBook}
                      size='middle'
                      min={0}
                      precision={0}
                      value={quantity}
                      onChange={(value) => handleOptionalExtraChange(value, ID)}
                    />
                  </div>
                </div>
              ))}
              <Divider style={s.sidebar.divider} />
            </>
          )}
          <div style={s.sidebar.costs}>
            <Text strong>{t('ProposalModal.fixedCost', 'Fixed cost')}</Text>
            <Text style={s.sidebar.value}>{formatCents(currency, baseCostTotal)}</Text>
          </div>
          <div style={s.sidebar.costs}>
            <Text strong>{t('ProposalModal.extras', 'Extras')}</Text>
            <Text style={s.sidebar.value}>{formatCents(currency, optionalExtrasTotal)}</Text>
          </div>
          <Divider style={s.sidebar.divider} />
          <div style={s.sidebar.totalCost}>
            <Title level={5}>{t('ProposalModal.total', 'Total (ex VAT)')}</Title>
            <Text strong style={s.sidebar.value}>
              {formatCents(currency, optionalExtrasTotal + baseCostTotal)}
            </Text>
          </div>
          <Button
            disabled={!canRequestToBook}
            type='primary'
            style={s.sidebar.buttons}
            onClick={async () => await handleRequestToBook()}
            loading={fetching}>
            {t('ProposalModal.requestBook', 'Request to book')}
          </Button>
          {error && <LoadError error={error} />}
          {/* <ContactDetails
            compact
            firstName={accountManager?.firstName}
            email={accountManager?.email}
            phone={accountManager?.phoneNumber}
          /> */}
        </div>
      </div>
    </AntModal>
  )
}

export const ProposalModal = withRouter(ProposalModalWithoutRouting)
